import { useState, useEffect } from 'react'
import {
  HiOutlineX,
  HiOutlineLocationMarker,
  HiOutlinePhotograph,
  HiOutlineLink,
  HiOutlineTag,
  HiOutlineHome,
  HiOutlineCurrencyDollar,
  HiOutlineCheck,
} from 'react-icons/hi'
import clsx from 'clsx'

// Marker type options
const markerTypeOptions = [
  { value: 'custom', label: 'Tự do', color: '#10B981', icon: '🏷️', description: 'Đánh dấu tự do' },
  { value: 'ban', label: 'Bán', color: '#EF4444', icon: '💰', description: 'BĐS đang bán' },
  { value: 'thue', label: 'Cho thuê', color: '#3B82F6', icon: '🏠', description: 'BĐS cho thuê' },
  { value: 'both', label: 'Bán & Thuê', color: '#8B5CF6', icon: '🔄', description: 'Cả bán và thuê' },
]

// Property type options
const propertyTypeOptions = [
  { value: 'dat_nen', label: 'Đất nền' },
  { value: 'nha_pho', label: 'Nhà phố' },
  { value: 'can_ho', label: 'Căn hộ' },
  { value: 'biet_thu', label: 'Biệt thự' },
  { value: 'nha_xuong', label: 'Nhà xưởng' },
  { value: 'van_phong', label: 'Văn phòng' },
  { value: 'khac', label: 'Khác' },
]

// Color presets
const colorPresets = [
  { name: 'Xanh dương', value: '#3B82F6' },
  { name: 'Đỏ', value: '#EF4444' },
  { name: 'Xanh lá', value: '#10B981' },
  { name: 'Tím', value: '#8B5CF6' },
  { name: 'Cam', value: '#F59E0B' },
  { name: 'Hồng', value: '#EC4899' },
  { name: 'Xám', value: '#6B7280' },
]

function MobileMarkerModal({
  isOpen,
  onClose,
  onSave,
  initialData = null,
  isEdit = false,
}) {
  const [step, setStep] = useState(1) // 1: Type, 2: Info, 3: Details
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    address: '',
    marker_type: 'custom',
    property_type: '',
    price_text: '',
    rent_price_text: '',
    image_url: '',
    description: '',
    area_text: '',
    external_url: '',
    latitude: null,
    longitude: null,
    marker_color: '#3B82F6',
    label_text: '',
  })

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setForm({
        id: initialData.id || null,
        name: initialData.name || '',
        address: initialData.address || '',
        marker_type: initialData.marker_type || 'custom',
        property_type: initialData.property_type || '',
        price_text: initialData.price_text || '',
        rent_price_text: initialData.rent_price_text || '',
        image_url: initialData.image_url || '',
        description: initialData.description || '',
        area_text: initialData.area_text || '',
        external_url: initialData.external_url || '',
        latitude: initialData.latitude,
        longitude: initialData.longitude,
        marker_color: initialData.marker_color || '#3B82F6',
        label_text: initialData.label_text || '',
      })
      // Skip type selection when editing existing marker
      setStep(isEdit ? 2 : 1)
    }
  }, [isOpen, initialData, isEdit])

  const handleSave = async () => {
    if (!form.name.trim()) {
      // Simple validation
      return
    }

    setSaving(true)
    try {
      await onSave?.(form)
      onClose()
    } catch (error) {
      console.error('Save failed:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleTypeSelect = (type) => {
    setForm((prev) => ({
      ...prev,
      marker_type: type,
      marker_color: markerTypeOptions.find((o) => o.value === type)?.color || '#3B82F6',
    }))
    setStep(2)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step > 1 && !isEdit && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="font-semibold text-gray-900">
              {isEdit ? 'Chỉnh sửa marker' : 'Thêm marker mới'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Step indicator */}
        {!isEdit && (
          <div className="px-4 py-2 border-b border-gray-50">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={clsx(
                    'flex-1 h-1 rounded-full transition-colors',
                    s <= step ? 'bg-primary-500' : 'bg-gray-200'
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1.5 text-center">
              {step === 1 && 'Chọn loại marker'}
              {step === 2 && 'Thông tin cơ bản'}
              {step === 3 && 'Chi tiết thêm'}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {/* Step 1: Type selection */}
          {step === 1 && (
            <div className="p-4 space-y-3">
              {markerTypeOptions.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelect(type.value)}
                  className={clsx(
                    'w-full p-4 rounded-xl border-2 transition-all text-left',
                    form.marker_type === type.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${type.color}15` }}
                    >
                      {type.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{type.label}</span>
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{type.description}</p>
                    </div>
                    {form.marker_type === type.value && (
                      <HiOutlineCheck className="w-5 h-5 text-primary-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Basic info */}
          {step === 2 && (
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tên marker <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="VD: Đất nền KDC Hòa Xuân..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Địa chỉ
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Địa chỉ cụ thể..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Custom marker: Label text */}
              {form.marker_type === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nhãn hiển thị
                  </label>
                  <input
                    type="text"
                    value={form.label_text}
                    onChange={(e) => setForm({ ...form, label_text: e.target.value })}
                    placeholder="VD: 5tr/m², Dự án ABC..."
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* BĐS markers: Price fields */}
              {form.marker_type !== 'custom' && (
                <>
                  {/* Property type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Loại BĐS
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {propertyTypeOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setForm({ ...form, property_type: opt.value })}
                          className={clsx(
                            'px-3 py-2 text-sm rounded-lg border transition-all',
                            form.property_type === opt.value
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sale price */}
                  {(form.marker_type === 'ban' || form.marker_type === 'both') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <HiOutlineCurrencyDollar className="w-4 h-4 inline mr-1 text-red-500" />
                        Giá bán
                      </label>
                      <input
                        type="text"
                        value={form.price_text}
                        onChange={(e) => setForm({ ...form, price_text: e.target.value })}
                        placeholder="VD: 2.5 tỷ, 15tr/m²..."
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {/* Rent price */}
                  {(form.marker_type === 'thue' || form.marker_type === 'both') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <HiOutlineHome className="w-4 h-4 inline mr-1 text-blue-500" />
                        Giá thuê
                      </label>
                      <input
                        type="text"
                        value={form.rent_price_text}
                        onChange={(e) => setForm({ ...form, rent_price_text: e.target.value })}
                        placeholder="VD: 15tr/tháng..."
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {/* Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Diện tích
                    </label>
                    <input
                      type="text"
                      value={form.area_text}
                      onChange={(e) => setForm({ ...form, area_text: e.target.value })}
                      placeholder="VD: 100m², 5x20m..."
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}

              {/* Marker color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Màu marker
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setForm({ ...form, marker_color: color.value })}
                      className={clsx(
                        'w-10 h-10 rounded-xl transition-all',
                        form.marker_color === color.value && 'ring-2 ring-offset-2 ring-gray-400'
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Additional details */}
          {step === 3 && (
            <div className="p-4 space-y-4">
              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <HiOutlinePhotograph className="w-4 h-4 inline mr-1" />
                  Link ảnh
                </label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {form.image_url && (
                  <div className="mt-2 relative w-24 h-24 rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={form.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* External URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <HiOutlineLink className="w-4 h-4 inline mr-1" />
                  Link chi tiết
                </label>
                <input
                  type="url"
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  placeholder="Link bài đăng, landing page..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mô tả
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Thông tin thêm về BĐS..."
                  rows={3}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Position info */}
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <HiOutlineLocationMarker className="w-4 h-4" />
                  <span>
                    {form.latitude?.toFixed(6)}, {form.longitude?.toFixed(6)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 safe-bottom">
          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={!form.marker_type}
              className="w-full py-3.5 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              Tiếp tục
            </button>
          ) : step === 2 ? (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(3)}
                className="flex-1 py-3.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Thêm chi tiết
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
                className="flex-1 py-3.5 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleSave}
              disabled={!form.name.trim() || saving}
              className="w-full py-3.5 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Hoàn tất'}
            </button>
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  )
}

export default MobileMarkerModal
